import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DeleteProduct, deleteRunById } from 'src/common/services/api/DeleteRunService';
import { Logger } from 'src/common/services/Logger';

import { ENTITY_BY_PRODUCT, QUERY_KEY_BY_PRODUCT } from './useDeleteRun.constants';
import { useInstructionPolling } from './useInstructionPolling';

export const useDeleteRun = (
	product: DeleteProduct
): {
	deleteRun: (runId: string) => Promise<void>;
	isDeleting: boolean;
} => {
	const queryClient = useQueryClient();
	const [isPolling, setIsPolling] = useState(false);

	const invalidateQuery = (): void => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BY_PRODUCT[product]] });
	};

	const handleSettled = (): void => {
		setIsPolling(false);
	};

	const { startPolling } = useInstructionPolling(
		product,
		ENTITY_BY_PRODUCT[product],
		invalidateQuery,
		handleSettled
	);

	const { mutateAsync, status } = useMutation((runId: string) => deleteRunById(product, runId), {
		onSuccess: (response) => {
			const instructionIds = response.states.map((state) => state.id);
			if (instructionIds.length > 0) {
				setIsPolling(true);
			}
			startPolling(instructionIds);
		},
		onError: (error) => {
			Logger.error('useDeleteRun: failed to delete run', { product, error });
		}
	});

	const deleteRun = async (runId: string): Promise<void> => {
		await mutateAsync(runId);
	};

	const isDeleting = status === 'loading' || isPolling;

	return { deleteRun, isDeleting };
};
